// Actividad por hora del día (0–23): barras de mensajes recibidos.
// El backend garantiza 24 entradas ya rellenas, pero se normaliza igual por si
// llega un payload parcial: un hueco en el eje descuadraría las 24 barras.

import { memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { Card } from "../../../../components/ui/Card";
import useIsMobile from "../../../../hooks/useIsMobile";
import { useChartColors } from "./chartTheme";
import { formatAxisCount, formatInt } from "./format";

// Solo 5 ticks: con 24 etiquetas de 11px el eje se vuelve ruido.
const HOUR_TICKS = [0, 6, 12, 18, 23];

const fmtHourTick = (h) => `${h}h`;
const fmtHourLabel = (h) => `${String(h).padStart(2, "0")}:00`;
const fmtTooltipValue = (value, name) => [formatInt(value), name];

/**
 * @param {object} props
 * @param {Array<{hour: number, count: number}>} props.rows - por_hora del backend
 * @param {string} [props.className]
 */
function HourlyChart({ rows, className }) {
  const { t } = useTranslation();
  const { colors, axisTick, gridProps, tooltipStyle, tooltipLabelStyle } = useChartColors();
  // D3 · El ensanchado del eje Y es SÓLO de teléfono (ver comentario del <YAxis>).
  const isMobile = useIsMobile();

  const data = useMemo(() => {
    const byHour = new Map((rows || []).map((r) => [r.hour, r.count]));
    return Array.from({ length: 24 }, (_, hour) => ({
      hour,
      count: byHour.get(hour) || 0,
    }));
  }, [rows]);

  const hasData = (rows || []).some((r) => (r?.count || 0) > 0);

  return (
    <Card padding="md" className={className}>
      <h2 className="text-sm font-semibold text-fg mb-3">{t("Actividad por hora")}</h2>

      {hasData ? (
        <div
          role="img"
          aria-label={t("Gráfico de barras con los mensajes recibidos por hora del día.")}
        >
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={data} margin={{ top: 8, right: 4, bottom: 0, left: isMobile ? 0 : -12 }}>
              <CartesianGrid {...gridProps} />
              <XAxis
                dataKey="hour"
                axisLine={false}
                tickLine={false}
                tick={axisTick}
                ticks={HOUR_TICKS}
                tickFormatter={fmtHourTick}
              />
              {/* A6/D3 · SÓLO en teléfono el eje pasa a `width` 44 con
                  `margin.left: 0`: con `left: -12` y `width={40}` el eje se
                  salía del SVG y los rótulos se cortaban 4px por la izquierda
                  ("800" se leía "300"). A 11px de tipografía "2,4k" mide 25px
                  más los 5px de tickMargin, así que 44 los contiene enteros.
                  Recharts recibe estos dos valores como props de JS, no como
                  clases: sin el gate de viewport se aplicaban también en
                  escritorio y el área de trazado se estrechaba 16px a 1280 y a
                  1920 (medido: x=578 w=369 → x=594 w=353). `useIsMobile()`
                  devuelve false hasta montar, así que el primer paint de
                  escritorio ya sale con la geometría del baseline. */}
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={axisTick}
                allowDecimals={false}
                width={isMobile ? 44 : 40}
                tickFormatter={formatAxisCount}
              />
              <Tooltip
                contentStyle={tooltipStyle}
                labelStyle={tooltipLabelStyle}
                formatter={fmtTooltipValue}
                labelFormatter={fmtHourLabel}
                cursor={{ fill: colors.bgMuted }}
              />
              <Bar
                dataKey="count"
                name={t("Recibidos")}
                fill={colors.brand}
                radius={[3, 3, 0, 0]}
                maxBarSize={18}
                isAnimationActive={false}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="h-[280px] flex items-center justify-center text-[12px] md:text-[11px] text-fg-muted">
          {t("Sin mensajes en el período.")}
        </div>
      )}
    </Card>
  );
}

export default memo(HourlyChart);
